import React, { useState, useEffect, useRef } from "react";
import { useConfirm } from "../../../../components/ui/Confirm";

const API = process.env.REACT_APP_API_URL;

const BATCH_NAMES = {
  1: "Alpha — Calibration initiale",
  2: "Beta — Nuances émotionnelles",
  3: "Gamma — Sarcasme & Ambiguïté",
  4: "Delta — Validation finale",
};

const BATCH_THRESHOLDS = { 1: 50, 2: 55, 3: 60, 4: 65 };
const MIN_MESSAGES = 4;

export default function TrainingMonitor({ sessionCode, onComplete }) {
  const confirm = useConfirm();

  const [active, setActive] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(1);
  const [batchProgress, setBatchProgress] = useState({});
  const [totalScore, setTotalScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [totalProcessed, setTotalProcessed] = useState(0);
  const [undoAvailable, setUndoAvailable] = useState(false);
  const [canUnlock, setCanUnlock] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [starting, setStarting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const completedRef = useRef(false);

  const [timeBoostsRemaining, setTimeBoostsRemaining] = useState(2);
  const [hintUsed, setHintUsed] = useState(false);

  // ── Activité J4 ──────────────────────────────────────────────────────────
  const prevTotalProcessedRef = useRef(null);
  const lastJ4ActivityRef = useRef(null);
  const [j4Active, setJ4Active] = useState(false);

  // ── Heartbeat J3 → serveur ───────────────────────────────────────────────
  // Tant que la modale TrainingMonitor est montée ET que la session est active,
  // on ping le serveur toutes les 3s pour signaler que J3 est présent.
  // Si le ping s'arrête, le serveur met supervisorLastSeen à null après timeout.
  // Côté J4, on lit supervisorLastSeen dans le polling pour détecter l'absence.
  const heartbeatRef = useRef(null);

  const startHeartbeat = () => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(async () => {
      try {
        await fetch(`${API}/api/sessions/${sessionCode}/training/supervisor-heartbeat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
      } catch { /* silencieux */ }
    }, 3000);
  };

  const stopHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  // Démarre le heartbeat quand la session est active, l'arrête sinon
  useEffect(() => {
    if (active) { startHeartbeat(); }
    else { stopHeartbeat(); }
    return () => stopHeartbeat();
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Arrête le heartbeat au démontage du composant (modale fermée)
  useEffect(() => {
    return () => stopHeartbeat();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchInitialState = async () => {
      try {
        const res = await fetch(`${API}/api/sessions/${sessionCode}/training/state`);
        const data = await res.json();
        if (data.active) {
          setActive(true);
          setCurrentBatch(data.currentBatch);
          setBatchProgress(data.batchProgress);
          setTotalScore(data.totalScore);
          setStreak(data.streak);
          setTotalProcessed(data.totalProcessed);
          setUndoAvailable(data.undoAvailable);
          const cbd = data.batchProgress[data.currentBatch] || {};
          setTimeBoostsRemaining(2 - (cbd.timeBoostsUsed || 0));
          setHintUsed((cbd.hintsUsed || 0) >= 1);
        }
        if (data.completed) {
          completedRef.current = true;
          setCompleted(true);
          setActive(false);
        }
      } catch (error) {
        console.error("Fetch initial state error:", error);
      }
    };
    fetchInitialState();
  }, [sessionCode]);

  useEffect(() => {
    if (!active && !completed) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/sessions/${sessionCode}/training/state`);
        const data = await res.json();

        setCurrentBatch(data.currentBatch);
        setBatchProgress(data.batchProgress);
        setTotalScore(data.totalScore);
        setStreak(data.streak);
        setTotalProcessed(data.totalProcessed);
        setUndoAvailable(data.undoAvailable);

        const cbd = data.batchProgress[data.currentBatch] || {};
        setTimeBoostsRemaining(2 - (cbd.timeBoostsUsed || 0));
        setHintUsed((cbd.hintsUsed || 0) >= 1);

        const messages = cbd.messages || [];
        const correctCount = messages.filter(m => m.correct).length;
        const accuracy = messages.length > 0 ? (correctCount / messages.length) * 100 : 0;
        const threshold = BATCH_THRESHOLDS[data.currentBatch] || 50;
        setCanUnlock(messages.length >= MIN_MESSAGES && accuracy >= threshold);

        // Détection activité J4
        if (prevTotalProcessedRef.current !== null && data.totalProcessed > prevTotalProcessedRef.current) {
          lastJ4ActivityRef.current = Date.now();
          setJ4Active(true);
        }
        prevTotalProcessedRef.current = data.totalProcessed;
        if (lastJ4ActivityRef.current && Date.now() - lastJ4ActivityRef.current > 8000) {
          setJ4Active(false);
        }

        if (data.completed && !completedRef.current) {
          completedRef.current = true;
          setCompleted(true);
          setActive(false);
          onComplete?.();
        }
        if (!data.active && active && !data.completed) setActive(false);
      } catch (error) {
        console.error("Monitor polling error:", error);
      }
    }, 300);

    return () => clearInterval(interval);
  }, [active, completed, sessionCode, onComplete]);

  const handleStart = async () => {
    if (completed) return;
    setStarting(true);
    try {
      const res = await fetch(`${API}/api/sessions/${sessionCode}/training/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ playerId: 3 }),
      });
      if (res.ok) { setActive(true); setCompleted(false); completedRef.current = false; }
    } catch (error) { console.error("Start error:", error); }
    finally { setStarting(false); }
  };

  const handleReset = async () => {
    const ok = await confirm({
      title: "Réinitialiser la calibration ?",
      message: "Toute la progression en cours sera perdue. Cette action est irréversible.",
      confirmText: "Réinitialiser", cancelText: "Annuler", danger: true,
    });
    if (!ok) return;
    setResetting(true);
    try {
      const res = await fetch(`${API}/api/sessions/${sessionCode}/training/reset`, {
        method: "POST", headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        setActive(false); setCompleted(false); completedRef.current = false;
        setCurrentBatch(1); setBatchProgress({}); setTotalScore(0);
        setStreak(0); setTotalProcessed(0); setUndoAvailable(false);
        setCanUnlock(false); setTimeBoostsRemaining(2); setHintUsed(false);
        setJ4Active(false); prevTotalProcessedRef.current = null; lastJ4ActivityRef.current = null;
      }
    } catch (error) { console.error("Reset error:", error); }
    finally { setResetting(false); }
  };

  const handleTimeBoost = async () => {
    try { await fetch(`${API}/api/sessions/${sessionCode}/training/time-boost`, { method: "POST" }); }
    catch (error) { console.error("Time boost error:", error); }
  };

  const handleHint = async () => {
    try {
      const res = await fetch(`${API}/api/sessions/${sessionCode}/training/hint`, { method: "POST" });
      const data = await res.json();
      alert(data.hint);
    } catch (error) { console.error("Hint error:", error); }
  };

  const handleUnlockBatch = async () => {
    try {
      const res = await fetch(`${API}/api/sessions/${sessionCode}/training/unlock-batch`, { method: "POST" });
      if (!res.ok) { const d = await res.json(); alert(d.error || "Erreur lors du déverrouillage"); return; }
      const data = await res.json();
      if (data.completed) { setCompleted(true); setActive(false); onComplete?.(); }
    } catch (error) {
      console.error("Unlock batch error:", error);
      alert("Erreur réseau lors du déverrouillage");
    }
  };

  const handleUndo = async () => {
    try { await fetch(`${API}/api/sessions/${sessionCode}/training/undo`, { method: "POST" }); }
    catch (error) { console.error("Undo error:", error); }
  };

  const ResetButton = () => (
    <button onClick={handleReset} disabled={resetting}
      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-rose-500/60 bg-rose-500/10 text-rose-300 text-sm font-semibold hover:bg-rose-500/20 hover:border-rose-400 transition disabled:opacity-40 disabled:cursor-not-allowed">
      <span className="text-base">↺</span>
      <span>{resetting ? "Réinitialisation en cours…" : "Réinitialiser la calibration"}</span>
    </button>
  );

  const J4StatusBadge = () => (
    <div className={["flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-mono transition-all",
      j4Active ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" : "bg-amber-500/10 border-amber-500/40 text-amber-300",
    ].join(" ")}>
      <div className={["w-2 h-2 rounded-full flex-shrink-0", j4Active ? "bg-emerald-400 animate-pulse" : "bg-amber-400"].join(" ")} />
      <span>
        {j4Active
          ? `Opérateur actif — ${totalProcessed} message${totalProcessed > 1 ? "s" : ""} traité${totalProcessed > 1 ? "s" : ""}`
          : totalProcessed > 0
            ? `Opérateur en pause — ${totalProcessed} message${totalProcessed > 1 ? "s" : ""} traité${totalProcessed > 1 ? "s" : ""}`
            : "En attente de l'opérateur (salle Open Space)"}
      </span>
    </div>
  );

  // ── Écran succès cyber ───────────────────────────────────────────────────
  if (completed) {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950/30 border border-emerald-500/30 p-8">
        {/* Grille de fond */}
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: "linear-gradient(rgba(52,211,153,1) 1px, transparent 1px), linear-gradient(90deg, rgba(52,211,153,1) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }} />
        {/* Halo */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(52,211,153,0.18) 0%, transparent 65%)" }} />

        <div className="relative space-y-6">
          {/* Titre */}
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-[11px] font-mono text-emerald-400 uppercase tracking-[0.2em]">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Calibration terminée
            </div>
            <div className="text-3xl font-bold text-white font-mono tracking-tight mt-2">
              MODULE<span className="text-emerald-400">_</span>EMPATHIE
            </div>
            <div className="text-sm text-emerald-300 font-mono">Validation complète — 4/4 lots traités</div>
          </div>

          {/* Code d'accès */}
          <div className="rounded-xl border border-emerald-500/40 bg-black/40 p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono mb-3">
              🔓 Code d'accès généré
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="font-mono text-2xl font-bold text-emerald-300 tracking-widest">
                EMPATH<span className="text-slate-500">-</span>4729
              </div>
              <div className="text-[10px] font-mono text-slate-500 text-right leading-relaxed">
                Dossier <span className="text-amber-400">.ingratitude</span><br />
                Salle des archives
              </div>
            </div>
          </div>

          {/* Barre de progression batches */}
          <div className="grid grid-cols-4 gap-2">
            {[1,2,3,4].map(i => (
              <div key={i} className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-2 text-center">
                <div className="text-[10px] font-mono text-emerald-400 mb-1">Lot {i}</div>
                <div className="text-base">✓</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────

  const currentBatchData = batchProgress[currentBatch] || {};
  const currentMessages = currentBatchData.messages || [];
  const correctCount = currentMessages.filter(m => m.correct).length;
  const batchAccuracy = currentMessages.length > 0 ? (correctCount / currentMessages.length) * 100 : 0;
  const currentThreshold = BATCH_THRESHOLDS[currentBatch] || 50;

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-xl bg-gradient-to-r from-slate-900 to-slate-800 border border-cyan-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={["w-3 h-3 rounded-full", active ? "bg-emerald-500 animate-pulse" : "bg-slate-600"].join(" ")} />
            <div className="text-sm font-mono text-cyan-300">SUPERVISION CALIBRATION</div>
          </div>
          <div className={["px-3 py-1 rounded-full text-xs font-mono font-bold border",
            active ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/50" : "bg-slate-700/50 text-slate-400 border-slate-600"
          ].join(" ")}>
            {active ? `LOT ${currentBatch}/4` : "STANDBY"}
          </div>
        </div>
      </div>

      {!active && (
        <div className="space-y-4">
          <div className="p-6 rounded-xl bg-slate-900/30 border border-white/5">
            <div className="grid grid-cols-4 gap-2 mb-4">
              {[1,2,3,4].map(i => (
                <div key={i} className="p-3 rounded-lg bg-slate-800/30 border border-white/5 text-center opacity-30">
                  <div className="text-xs text-slate-500 mb-1">Lot {i}</div>
                  <div className="text-2xl">🔒</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[["Précision","---%"],["Messages","--/"+MIN_MESSAGES],["Série","--"]].map(([label,val]) => (
                <div key={label} className="p-3 rounded-lg bg-slate-800/30 border border-white/5">
                  <div className="text-xs text-slate-500 mb-1">{label}</div>
                  <div className="text-xl font-mono text-slate-600">{val}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="p-6 rounded-xl border-2 border-cyan-500/30 bg-gradient-to-br from-cyan-950/20 to-blue-950/20">
            <div className="text-center space-y-4">
              <div className="text-sm font-mono text-cyan-300 uppercase tracking-wider">Système de calibration prêt</div>
              <button onClick={handleStart} disabled={starting}
                className="w-full group relative overflow-hidden rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 p-[1px] transition-all hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] disabled:opacity-50">
                <div className="relative bg-slate-900 rounded-[7px] px-6 py-3 group-hover:bg-slate-900/80 transition-all">
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                    <span className="font-mono text-sm text-cyan-300">
                      {starting ? "INITIALISATION EN COURS..." : "INITIALISER SÉQUENCE"}
                    </span>
                  </div>
                </div>
              </button>
              <div className="text-xs text-slate-400 font-mono">4 lots · {MIN_MESSAGES} messages min · Seuil variable</div>
            </div>
          </div>

          <ResetButton />

          <div className="p-4 rounded-lg bg-blue-950/20 border border-blue-500/30">
            <div className="text-xs text-blue-200 mb-2 font-mono">📋 Instructions superviseur</div>
            <div className="text-xs text-slate-300 space-y-1 font-mono">
              <p>• Initialisez la séquence pour activer le premier lot</p>
              <p>• Suivez les métriques en temps réel de l'opérateur</p>
              <p>• Déverrouillez dès {MIN_MESSAGES} messages traités et seuil atteint</p>
              <p>• Utilisez les power-ups stratégiquement</p>
              <p>• En cas de blocage : réinitialisez la calibration</p>
            </div>
          </div>
        </div>
      )}

      {active && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-amber-400/50 bg-amber-500/10">
            <span className="text-amber-400 text-lg flex-shrink-0 mt-0.5">⚠️</span>
            <p className="text-xs text-amber-200 leading-relaxed">
              <span className="font-semibold">Ne fermez pas cette fenêtre</span> pendant que votre coéquipier effectue le tri — cela interromprait la session de calibration.
            </p>
          </div>

          <J4StatusBadge />

          <div className="p-6 rounded-xl bg-slate-900/50 border border-cyan-500/30">
            <div className="text-sm text-slate-400 mb-1">Lot actuel</div>
            <div className="text-xl font-bold text-white mb-4">{BATCH_NAMES[currentBatch]}</div>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Précision</span>
                <span className="font-mono text-2xl text-white">{batchAccuracy.toFixed(1)}%</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Seuil requis</span>
                <span className={batchAccuracy >= currentThreshold ? "text-emerald-400 font-semibold" : "text-amber-400"}>
                  {currentThreshold}%{batchAccuracy >= currentThreshold ? " ✓ atteint" : ""}
                </span>
              </div>
              <div className="h-6 bg-slate-800 rounded-full overflow-hidden relative border border-white/10">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-500"
                  style={{ width: `${Math.min(100, batchAccuracy)}%` }}>
                  <div className="absolute inset-0 bg-white/20 animate-pulse" />
                </div>
                <div className="absolute top-0 bottom-0 w-0.5 bg-amber-400/70" style={{ left: `${currentThreshold}%` }} />
              </div>
              <div className="text-xs text-slate-400">
                Messages traités : {currentMessages.length}
                <span className="text-slate-600"> / {MIN_MESSAGES} min pour déverrouiller</span>
                {currentMessages.length >= MIN_MESSAGES && <span className="text-emerald-400 ml-2">✓</span>}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={handleTimeBoost} disabled={timeBoostsRemaining <= 0}
              className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition text-white font-semibold text-xs">
              ⏱️ Ajouter +2s ({timeBoostsRemaining})
            </button>
            <button onClick={handleUndo} disabled={!undoAvailable}
              className="px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-30 disabled:cursor-not-allowed transition text-white font-semibold text-xs">
              🔄 Annuler l'erreur
            </button>
          </div>

          <button onClick={handleUnlockBatch} disabled={!canUnlock}
            className="w-full px-6 py-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:opacity-30 disabled:cursor-not-allowed transition text-white font-bold">
            {currentBatch === 4 ? "🏁 TERMINER CALIBRATION" : "▶️ DÉVERROUILLER LOT SUIVANT"}
          </button>

          <ResetButton />

          <div className="grid grid-cols-3 gap-3 text-center text-sm">
            <div className="p-3 rounded-lg bg-slate-800/50">
              <div className="text-slate-400 mb-1">Série</div>
              <div className="text-xl font-bold text-white">{streak > 0 && "🔥 "}{streak}</div>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50">
              <div className="text-slate-400 mb-1">Traités</div>
              <div className="text-xl font-bold text-white">{totalProcessed}</div>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50">
              <div className="text-slate-400 mb-1">Erreurs</div>
              <div className="text-xl font-bold text-red-300">{currentBatchData.errors || 0}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}